import { useEffect, useRef } from "react";
import { trackOnce } from "../track";
import type { Language, LeadKind, PieceRole, PreviewPiece } from "../types";
import type { Words } from "./copy";
import { FromPrice } from "./Facts";
import { KIND_KEY } from "./kinds";
import { MultiBuyOffer, multiBuyWords, offVars } from "./Quantity";
import "./rows.css";

/** One of the other pieces of the page, as the row offers it. */
export interface Suggestion {
  piece: PreviewPiece;
  kind: LeadKind;
  role: PieceRole;
  /** Its drawing, or the photo it was drawn from. */
  drawingUrl: string | null;
}

/** "Also from your photos": the pieces of a page of several that the page is
 *  not showing, one compact row each, under the price of the one it is.
 *
 *  A couple who asked for the two of them was shown him, her and only then the
 *  Pair, eight phone screens of three equal cards; the page is now about one
 *  piece and the rest are here, one tap away. They are of two kinds, and the
 *  difference is whether the customer is choosing between them or adding:
 *
 *  - another product of the same people (a single beside their Pair, a head
 *    keychain beside a pet one): bought instead. "See this one" makes it the
 *    page, with the way back at the top.
 *  - another subject (a second pet's keychain, a dog beside a person's
 *    statue): bought as well. "Add this one too" opens the sheet that pays for
 *    both at once, in one parcel, which is where the multi-buy applies, so its
 *    line is said here and only here.
 *
 *  A page that cannot take the payment itself has no sheet to add to, and then
 *  every piece is "See this one": it has its own way to the shop. The page
 *  decides what a tap does (the address, the sheet); this draws the row and
 *  counts that it was reached, the way the price card is. */
export default function Suggestions({
  items,
  together,
  language,
  words: t,
  onSee,
  onAdd,
}: {
  items: Suggestion[];
  /** The sheet of "Order them together" can take the page's piece and this
   *  one: the page pays here and both are priced with their sizes. */
  together: boolean;
  language: Language;
  words: Words;
  onSee: (item: Suggestion) => void;
  onAdd: (item: Suggestion) => void;
}) {
  const ref = useRef<HTMLElement>(null);

  // Whether anyone gets this far is the question the row exists to answer.
  useEffect(() => {
    const node = ref.current;
    if (!node || !("IntersectionObserver" in window)) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) trackOnce("section.seen", { id: "suggestions" });
      },
      { threshold: 0.5 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [items.length > 0]);

  if (!items.length) return null;

  return (
    <section className="card suggest" ref={ref} aria-label={t.t("sugTitle")}>
      <h2>{t.t("sugTitle")}</h2>
      <ul className="suggest-list">
        {items.map((item) => {
          const { piece, kind } = item;
          const name = t.t(KIND_KEY[kind] ?? "kindMinime");
          const price = piece.price;
          // Added only where it can be: another subject, with a sheet to add
          // it to. The page's own piece (the way back to it) is never added.
          const add = item.role === "extra" && together && !!price?.sizes?.length;
          const multi = add ? (price?.multiBuy ?? null) : null;
          const multiSaid = multi ? multiBuyWords(multi, t, language) : null;
          const go = () => (add ? onAdd(item) : onSee(item));
          return (
            <li className="suggest-item" key={piece.id}>
              {/* The picture is the same tap as the button, for a bigger
                  target; out of the tab order and the reading order so the
                  one button is not announced twice. */}
              <button type="button" className="suggest-shot" tabIndex={-1} aria-hidden="true" onClick={go}>
                {item.drawingUrl && (
                  <img src={item.drawingUrl} alt="" width={56} height={84} loading="lazy" decoding="async" />
                )}
              </button>
              <div className="suggest-what">
                <strong className="small">{name}</strong>
                {price && (
                  <p>
                    <FromPrice price={price} words={t} />
                  </p>
                )}
                {add && <p className="tiny muted">{t.t("sugParcel")}</p>}
                <button
                  type="button"
                  className="btn btn-soft btn-sm"
                  aria-label={`${t.t(add ? "sugAdd" : "sugSee")} · ${name}`}
                  onClick={go}
                >
                  {t.t(add ? "sugAdd" : "sugSee")}
                </button>
              </div>
              {/* Inside a dated window with its end date and what it usually
                  is (multiBuyWords); standing, the plain line. */}
              {multi && multiSaid && (
                <MultiBuyOffer badge={t.t("multiBuyOff", offVars(multi.percent))}>{multiSaid.each}</MultiBuyOffer>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** The way back to the piece the page is about, at the top, while another is
 *  shown. */
export function BackChip({ name, onBack, words: t }: { name: string; onBack: () => void; words: Words }) {
  return (
    <div className="backchip">
      <button type="button" className="chip chip-sm" onClick={onBack}>
        <span aria-hidden="true">←</span> {t.t("sugBack", { name })}
      </button>
    </div>
  );
}
